/**
 * TokenService
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * TODO: NEEDS SECURITY REVIEW — CATEGORY 3 (DO NOT MODIFY WITHOUT APPROVAL)
 *
 * The following security concerns were identified in a second-pass audit
 * and require product/security team validation before changes:
 *
 * 1. TOKEN STORAGE (sessionStorage):
 *    Access tokens are stored in sessionStorage (SESSION_STORAGE_KEYS.ZEST).
 *    Storage is accessible to any XSS attack vector.
 *    RISK: Token theft via XSS.
 *    RECOMMENDATION: Move to httpOnly cookies (requires backend change).
 *    DECISION REQUIRED: Security team must approve storage migration.
 *
 * 2. TOKEN IN URL PARAMETERS:
 *    Methods like sendDataToPaymentApp(), sendDataToRepay() use
 *    btoa(JSON.stringify(token)) to encode tokens in postMessage data
 *    and URL parameters. Tokens may appear in browser history, server logs,
 *    and referrer headers.
 *    RISK: Token exposure in transit.
 *    RECOMMENDATION: Use postMessage with structured data or header-based auth.
 *    DECISION REQUIRED: Does the payment app support token-in-header auth?
 *
 * 3. CUSTOMER ID IN URLS:
 *    getCustomerId() result is interpolated into API URLs as a path parameter.
 *    This means PII (customer UUID) appears in server access logs.
 *    RISK: PII exposure in logs.
 *    RECOMMENDATION: Accept customer ID via secure channel (auth header decode).
 *    DECISION REQUIRED: Can the API accept customer ID via a secure channel?
 *
 * 4. postMessage ORIGIN VALIDATION:
 *    The allowedOrigins check uses exact string matching. If origins change
 *    (e.g., new domains), messages will be silently dropped.
 *    RISK: Functional breakage or security bypass if origins misconfigured.
 *    RECOMMENDATION: Centralize origin configuration in environment files.
 *
 * NONE OF THESE ISSUES MAY BE CHANGED WITHOUT SECURITY TEAM SIGN-OFF.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { Token } from "../after-login/token/token";
import { JwtPayload, jwtDecode } from "jwt-decode";
import { GlobalParams } from "../after-login/common/tracking/aws-tracker";
import {
  PAYMENTAPPURL,
  QUICKPAYURL,
  BOLTMYACCOUNTSURL,
} from "../../environments/environment";
import { LeftPanelFlags } from "../after-login/services/LeftPanelService";
import { PersonalLoanPricing } from "../types/personal-loan.types";
import {
  CreditLimitStatusData,
  PLBannerFromZestLife,
  TripMoneyCreditLimitData,
  ExperianStaticData,
  ReferralData,
} from "../types/api.types";
import { PreapproveStates } from "../types/user.types";
import { LoanApplication } from "../types/loan.types";

declare let window: Window & {
  webengage?: {
    user: {
      login(userId: string): void;
      setAttribute(key: string, value: string | number | boolean): void;
    };
  };
};

export const SESSION_STORAGE_KEYS = {
  SIGN_UP: 'ngx-webstorage|zest-sign-up',
  CONTACT: 'ngx-webstorage|zest-contact',
  RESTRUCTURING_LA: 'ngx-webstorage|zest-restructuring-la',
  PARAMS: 'ngx-webstorage|zest-params',
  CHECKOUT_PARAMS: 'ngx-webstorage|zest-checkout-params',
  AUTHENTICATION: 'ngx-webstorage|zest-authentication', // 'ngx-webstorage|zest-authentication'
  ZEST: 'ngx-webstorage|zest', // Auth token after login
  ZEST_JOURNEY: 'ngx-webstorage|zest-journey', // Customer journey initiate response
  SOURCE: 'ngx-webstorage|zest-source',
} as const;


/** Session storage key to track if auto-token was invalidated (to prevent redirect loops) */
const AUTO_TOKEN_DISABLED_KEY = "zest-auto-token-disabled";

function isLocalDev(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.location?.hostname === "localhost" ||
      window.location?.hostname === "127.0.0.1")
  );
}

/** Check if auto-token generation is disabled (due to 401 or explicit logout) */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function isAutoTokenDisabled(): boolean {
  return (
    typeof window !== "undefined" &&
    sessionStorage.getItem(AUTO_TOKEN_DISABLED_KEY) === "true"
  );
}

export class TokenService {
  private storage = sessionStorage;

  getToken(): Token | null {
    let tokenData = this.storage.getItem(SESSION_STORAGE_KEYS.ZEST);
    // Native auth flow is now implemented — no auto-inject of dev tokens.
    // Users must log in via /authentication with phone number + OTP.
    return tokenData ? JSON.parse(tokenData) : null;
  }

  getAccessToken(): string | null {
    const token = this.getToken();
    return token && token.access_token ? token.access_token : null;
  }

  getLocalStorageToken = (): string | null => {
    const tokenData = this.storage.getItem(SESSION_STORAGE_KEYS.ZEST);
    if (!tokenData) return null;
    try {
      const token = JSON.parse(tokenData) as Token;
      return token?.access_token ?? null;
    } catch {
      return null;
    }
  };

  getLocalCustomerId = (): string | null => {
    const token = this.getLocalStorageToken();
    if (token) {
      try {
        const decoded = jwtDecode<JwtPayload>(token);
        return decoded ? decoded.sub ?? null : null;
      } catch {
        return null;
      }
    }
    return null;
  };

  getCustomerId(): string | null {
    const token = this.getToken();
    if (token && token.access_token) {
      try {
        const decoded = jwtDecode<JwtPayload>(token.access_token);
        return decoded ? decoded.sub ?? null : null;
      } catch {
        return null;
      }
    }
    return null;
  }

  setLeftPanelFlags(leftPanelFlags: LeftPanelFlags) {
    this.storage.setItem("left-panel", JSON.stringify(leftPanelFlags));
  }

  getLeftPanelFlags(): LeftPanelFlags | null {
    const result = this.storage.getItem("left-panel");
    return result ? JSON.parse(result) as LeftPanelFlags : null;
  }

  setPersonalLoanPricing(personalLoanPricing: PersonalLoanPricing[]) {
    this.storage.setItem("personal-loan", JSON.stringify(personalLoanPricing));
  }

  getPersonalLoanPricing(): PersonalLoanPricing[] | null {
    const result = this.storage.getItem("personal-loan");
    return result ? JSON.parse(result) as PersonalLoanPricing[] : null;
  }

  saveToSession(token: Token) {
    this.storage.setItem(SESSION_STORAGE_KEYS.ZEST, JSON.stringify(token));
    // Clear the auto-token disabled flag when a valid token is saved
    // This allows normal login flow to work after a logout
    if (isLocalDev()) {
      this.storage.removeItem(AUTO_TOKEN_DISABLED_KEY);
    }
  }

  removeAll() {
    // Disable auto-token generation before clearing
    if (isLocalDev()) {
      this.storage.setItem(AUTO_TOKEN_DISABLED_KEY, "true");
    }
    this.storage.clear();
    // Re-set the flag after clear (since clear removes everything)
    if (isLocalDev()) {
      this.storage.setItem(AUTO_TOKEN_DISABLED_KEY, "true");
    }
  }

  removeAllExceptToken() {
    const token: Token | null = this.getToken();
    this.storage.clear();
    if (token) {
      this.saveToSession(token);
    }
  }

  removeOnlyToken() {
    this.storage.removeItem(SESSION_STORAGE_KEYS.ZEST);
    // Disable auto-token generation to prevent redirect loops on localhost
    // This is set when explicitly removing token (e.g., on 401 or logout)
    if (isLocalDev()) {
      this.storage.setItem(AUTO_TOKEN_DISABLED_KEY, "true");
    }
  }

  savePostLoginRedirectComponentUrl(relativeComponentUrl: string | null) {
    if (relativeComponentUrl) {
      this.storage.setItem(
        "zest-post-login-redirect-component-url",
        relativeComponentUrl,
      );
    } else {
      this.storage.removeItem("zest-post-login-redirect-component-url");
    }
  }

  getPostLoginRedirectComponentUrl(): string | null {
    return this.storage.getItem("zest-post-login-redirect-component-url");
  }

  removePostLoginRedirectComponentUrl() {
    this.storage.removeItem("zest-post-login-redirect-component-url");
  }

  saveLoanApplication(application: LoanApplication) {
    this.storage.setItem("zest-application", JSON.stringify(application));
  }

  getLoanApplication(): LoanApplication | null {
    const result = this.storage.getItem("zest-application");
    return result ? JSON.parse(result) as LoanApplication : null;
  }

  saveCreditLimitStatus(data: CreditLimitStatusData) {
    this.storage.setItem("zest-credit-limit", JSON.stringify(data));
  }

  getCreditLimitStatus() {
    const result = this.storage.getItem("zest-credit-limit");
    return result ? JSON.parse(result)["status"] : null;
  }

  storePLDetailsFromZestLife(value: PLBannerFromZestLife) {
    this.storage.setItem("zest-pl-banner-zestlife", JSON.stringify(value));
  }

  getPLDetailsFromZestLife() {
    const result = this.storage.getItem("zest-pl-banner-zestlife");
    return result ? JSON.parse(result) : null;
  }

  saveTripMoneyCreditLimitStatus(data: TripMoneyCreditLimitData) {
    this.storage.setItem("zest-tripmoney-credit-limit", JSON.stringify(data));
  }

  getTripMoneyCreditLimitStatus() {
    const result = this.storage.getItem("zest-tripmoney-credit-limit");
    return result ? JSON.parse(result)["status"] : null;
  }

  getReferMerchantKey() {
    const token = this.getToken();
    return token && token.merchantKey ? (token.merchantKey as string) : null;
  }

  getMerchantCustomerId() {
    const token = this.getToken();
    return token && token.merchantCustomerId
      ? (token.merchantCustomerId as string)
      : null;
  }

  sendDataToPaymentApp(action: string, data: unknown) {
    const receiver = document.getElementById("receiver") as HTMLIFrameElement | null;
    if (receiver && receiver.contentWindow) {
      const dataToSend = JSON.stringify(data);
      receiver.contentWindow.postMessage(
        action + ":" + dataToSend,
        PAYMENTAPPURL,
      );
    }
  }

  sendDataToRepay(action: string, data: unknown) {
    const receiver = document.getElementById("repayreceiver") as HTMLIFrameElement | null;
    if (receiver && receiver.contentWindow) {
      const dataToSend = JSON.stringify(data);
      receiver.contentWindow.postMessage(
        action + ":" + dataToSend,
        QUICKPAYURL,
      );
    }
  }

  sendDataToMyAccountsV2(action: string, data: unknown) {
    const receiver = document.getElementById("myaccountsV2") as HTMLIFrameElement | null;
    if (receiver && receiver.contentWindow) {
      const dataToSend = JSON.stringify(data);
      receiver.contentWindow.postMessage(
        action + ":" + dataToSend,
        BOLTMYACCOUNTSURL,
      );
    }
  }

  receiveTokenAcknowledgementFromPaymentApp(action: string) {
    this.storage.setItem("zest-paymentapp-token-received", action);
  }

  receiveTokenAcknowledgementFromrepay(action: string) {
    this.storage.setItem("zest-repay-token-received", action);
  }

  receiveApplicationAcknowledgementFromPaymentApp(action: string) {
    this.storage.setItem("zest-paymentapp-application-received", action);
  }

  receiveApplicationAcknowledgementFromMyAccountsV2(action: string) {
    this.storage.setItem("zest-myaccountsv2-received", action);
  }

  checkTokenAcknowledgementFromMyAccountsV2() {
    return this.storage.getItem("zest-myaccountsv2-received");
  }

  checkTokenAcknowledgementFromPaymentApp() {
    return this.storage.getItem("zest-paymentapp-token-received");
  }

  checkTokenAcknowledgementFromrepay() {
    return this.storage.getItem("zest-repay-token-received");
  }

  checkApplicationAcknowledgementFromPaymentApp() {
    return this.storage.getItem("zest-paymentapp-application-received");
  }

  setReferral(response: ReferralData) {
    this.storage.setItem("zest-referral", JSON.stringify(response));
  }

  getReferralDetails() {
    const result = this.storage.getItem("zest-referral");
    return result ? JSON.parse(result) : null;
  }

  getGlobalParams(): GlobalParams | null {
    const result = this.storage.getItem(SESSION_STORAGE_KEYS.PARAMS);
    return result ? (JSON.parse(result) as GlobalParams) : null;
  }

  updateAndSetGlobalParams(type: string, value: string) {
    let globalParams = this.getGlobalParams();
    if (!globalParams) {
      globalParams = {} as GlobalParams;
    }
    (globalParams as unknown as Record<string, string>)[type] = value;
    this.storage.setItem(SESSION_STORAGE_KEYS.PARAMS, JSON.stringify(globalParams));
  }

  copyLinkAndToggleTaost(val: string) {
    const selBox = document.createElement("textarea");
    selBox.style.position = "fixed";
    selBox.style.left = "0";
    selBox.style.top = "0";
    selBox.style.opacity = "0";
    selBox.value = val;
    document.body.appendChild(selBox);

    const isiOSDevice = navigator.userAgent.match(/ipad|iphone/i);

    if (isiOSDevice) {
      const editable = selBox.contentEditable;
      const readOnly = selBox.readOnly;

      selBox.contentEditable = "true";
      selBox.readOnly = false;

      const range = document.createRange();
      range.selectNodeContents(selBox);

      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);

      selBox.setSelectionRange(0, 999999);
      selBox.contentEditable = editable;
      selBox.readOnly = readOnly;
    } else {
      selBox.focus();
      selBox.select();
    }

    document.execCommand("copy");
    document.body.removeChild(selBox);

    const x = document.getElementById("snackbar");
    if (x) {
      x.className = "show";
      setTimeout(() => {
        x.className = x.className.replace("show", "");
      }, 3000);
    }
  }

  setInArrearsLoanFlag(flag: boolean) {
    this.storage.setItem("zest-loan-due", JSON.stringify(flag));
  }

  getInArrearsLoanFlag() {
    const result = this.storage.getItem("zest-loan-due");
    return result ? JSON.parse(result) : null;
  }

  saveLoanType(merchantId?: string) {
    this.storage.setItem("zest-PL", merchantId || "");
  }

  getLoanType() {
    return this.storage.getItem("zest-PL");
  }

  setMandateStatus(mandateState: boolean | Record<string, unknown>) {
    this.storage.setItem(
      "zest-mandate-not-uploaded",
      JSON.stringify(mandateState),
    );
  }

  getMandateStatus() {
    const result = this.storage.getItem("zest-mandate-not-uploaded");
    return result ? JSON.parse(result) : null;
  }

  setKycVerificationStatus(kycVerificationStatus: boolean) {
    this.storage.setItem(
      "zest-kyc-verification-pending",
      JSON.stringify(kycVerificationStatus),
    );
  }

  getKycVerificationStatus() {
    const result = this.storage.getItem("zest-kyc-verification-pending");
    return result ? JSON.parse(result) : null;
  }

  setProfilePhotoFlag(isPhotoAvailable: boolean) {
    this.storage.setItem(
      "zest-photo-uploaded",
      JSON.stringify(isPhotoAvailable),
    );
  }

  getProfilePhotoFlag() {
    const result = this.storage.getItem("zest-photo-uploaded");
    return result ? JSON.parse(result) : null;
  }

  setPLEligibilityFlag(isZestPLCustomer: boolean) {
    this.storage.setItem("zest-PL-eligible", JSON.stringify(isZestPLCustomer));
  }

  getPLEligibiltyFlag() {
    const result = this.storage.getItem("zest-PL-eligible");
    return result ? JSON.parse(result) : null;
  }

  setVoucherGenFailedFor(data: string[]) {
    this.storage.setItem("zest-voucherGenFailedFor", JSON.stringify(data));
  }

  getVoucherGenFailedFor(): string[] {
    const result = this.storage.getItem("zest-voucherGenFailedFor");
    return result ? JSON.parse(result) : [];
  }

  setFKWhiteListStatus(isWhiteListCustomer: boolean) {
    this.storage.setItem(
      "zest-fk-whitelist",
      JSON.stringify(isWhiteListCustomer),
    );
  }

  getFKWhiteListStatus() {
    const result = this.storage.getItem("zest-fk-whitelist");
    return result ? JSON.parse(result) : null;
  }

  setReferralHideFlag(isPendingPointsAvailable: boolean) {
    this.storage.setItem(
      "zest-redeem-only",
      JSON.stringify(isPendingPointsAvailable),
    );
  }

  getReferralFlag() {
    const result = this.storage.getItem("zest-redeem-only");
    return result ? JSON.parse(result) : null;
  }

  setDefaultTab(tab: string) {
    this.storage.setItem("zest-default", tab);
  }

  getDefaultTab() {
    return this.storage.getItem("zest-default");
  }

  storeExperianStaticData(data: ExperianStaticData) {
    this.storage.setItem("zest-experian-data", JSON.stringify(data));
  }

  getExperianStaticData() {
    const result = this.storage.getItem("zest-experian-data");
    return result ? JSON.parse(result) : null;
  }

  setPreapproveStates(data: PreapproveStates) {
    this.storage.setItem("zest-preapprove-states", JSON.stringify(data));
  }

  getPreapproveStates() {
    const result = this.storage.getItem("zest-preapprove-states");
    return result ? JSON.parse(result) : null;
  }

  setSelectedWhiteListMerchant(merchant: string) {
    this.storage.setItem("zest-whitelist-merchant", merchant);
  }

  getSelectedWhitelistMerchant() {
    return this.storage.getItem("zest-whitelist-merchant");
  }

  setRiskCode(code: string) {
    this.storage.setItem("zest-risk", code);
  }

  getRiskCode() {
    return this.storage.getItem("zest-risk");
  }

  setMoratoriumFlag(flag: boolean) {
    this.storage.setItem("zest-emi-moratorium", JSON.stringify(flag));
  }

  getMoratoriumFlag() {
    const result = this.storage.getItem("zest-emi-moratorium");
    return result ? JSON.parse(result) : null;
  }

  setProcessingLoans(parentLoanId: string) {
    const currentList = this.getProcessingLoans();
    if (currentList.indexOf(parentLoanId) < 0) {
      currentList.push(parentLoanId);
    }
    this.storage.setItem("zest-processing-loan", JSON.stringify(currentList));
  }

  removeFromProcessingLoans(loanId: string) {
    const currentList = this.getProcessingLoans();
    const index = currentList.indexOf(loanId);
    if (index >= 0) {
      currentList.splice(index, 1);
    }
    this.storage.setItem("zest-processing-loan", JSON.stringify(currentList));
  }

  getProcessingLoans(): string[] {
    const list = this.storage.getItem("zest-processing-loan");
    return list ? JSON.parse(list) : [];
  }

  setBoltContext() {
    this.storage.setItem("zest-bolt-redirection", "true");
  }

  getBoltContext() {
    return this.storage.getItem("zest-bolt-redirection");
  }

  setGCRedirectContext() {
    this.storage.setItem("zest-gc-redirection", "true");
  }

  getGCRedirectContext() {
    return this.storage.getItem("zest-gc-redirection");
  }

  setMyAccountsV2UrlRoute(path: string) {
    this.storage.setItem("url_post_login", path);
  }

  getMyAccountsV2UrlRoute() {
    return this.storage.getItem("url_post_login");
  }

  setStorylyContext(path: string) {
    this.storage.setItem("storyly-context", path);
  }

  getStorylyContext() {
    return this.storage.getItem("storyly-context");
  }

  setAppMandateRedirect() {
    this.storage.setItem("app-mandate-redirect", "true");
  }

  getAppMandateRedirect() {
    return this.storage.getItem("app-mandate-redirect");
  }

  getParamString = (): string => {
    const token = this.getToken();
    const params: URLSearchParams = new URLSearchParams();
    if (token) {
      for (const key in token) {
        if (token.hasOwnProperty(key)) {
          params.set(key, String((token as Record<string, unknown>)[key]));
        }
      }
    }
    return params.toString();
  };

  setDeprecationFlag(flag = true) {
    this.storage.setItem("zest-myaccounts-deprecation", JSON.stringify(flag));
  }

  getDeprecationFlag(): boolean {
    const result = this.storage.getItem("zest-myaccounts-deprecation");
    return result ? JSON.parse(result) : false;
  }

  setWebEngageUserId(userId: string) {
    if (window.webengage) {
      window.webengage.user.login(userId);
    }
  }

  setWebEngageUserAttributes(attrs: Record<string, string | number | boolean>) {
    if (window.webengage) {
      Object.keys(attrs).forEach((key) => {
        window.webengage!.user.setAttribute(key, attrs[key]);
      });
    }
  }
}
